import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import Loader from '@zeus/core/Loader';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useProjectQaQueue } from '_/hooks/useProjects';
import { formatRelativeTime } from '_/utils/dateUtils';
import zeusSdk from '_/utils/SDKs';
import { Table, message, Button, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { ReactElement, useEffect, useCallback } from 'react';

interface IQaQueueItem {
  id: string;
  collectionSlug: string;
  collectionName: string;
  sourceSlug: string;
  sourceName: string;
  stoppedAt: string;
}

function QaQueue(): ReactElement {
  const { setBreadcrumbs, setOnRefresh, setRefreshLoading } = useTopHeader();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();

  const { data: qaqueue = [], isLoading, isRefetching, refetch } = useProjectQaQueue(selectedProject?.id);

  const handleStartQA = async (snapshotId: string) => {
    try {
      await zeusSdk.snapshots.assignToMe(snapshotId);
      message.success('Snapshot assigned to you');
      void refetch();
    } catch {
      message.error('Failed to assign snapshot');
    }
  };

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    setOnRefresh(handleRefresh);
    setRefreshLoading(isRefetching);

    return () => {
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [setOnRefresh, setRefreshLoading, isRefetching, handleRefresh]);

  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      { title: 'QA Queue' },
    ];

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs, selectedOrganization, selectedOrgSlug, selectedProject, selectedProjectSlug]);

  const columns: ColumnsType<IQaQueueItem> = [
    {
      title: 'Collection',
      dataIndex: 'collectionName',
      key: 'collectionName',
      render: (name: string, record: IQaQueueItem) => (
        <a href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${record.collectionSlug}`}>{name}</a>
      ),
      sorter: (a: IQaQueueItem, b: IQaQueueItem) => a.collectionName.localeCompare(b.collectionName),
    },
    {
      title: 'Source',
      dataIndex: 'sourceName',
      key: 'sourceName',
      render: (name: string, record: IQaQueueItem) => (
        <a
          href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${record.collectionSlug}/sources/${record.sourceSlug}`}
        >
          {name}
        </a>
      ),
      sorter: (a: IQaQueueItem, b: IQaQueueItem) => a.sourceName.localeCompare(b.sourceName),
    },
    {
      title: 'Lag',
      dataIndex: 'stoppedAt',
      key: 'stoppedAt',
      render: (stoppedAt: string) => formatRelativeTime(stoppedAt),
      sorter: (a: IQaQueueItem, b: IQaQueueItem) => new Date(a.stoppedAt).getTime() - new Date(b.stoppedAt).getTime(),
    },
    {
      title: '',
      key: 'actions',
      render: (_: any, record: IQaQueueItem) => (
        <Space>
          <Button type="primary" size="small" onClick={() => void handleStartQA(record.id)}>
            Start QA
          </Button>
          <a
            href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${record.collectionSlug}/sources/${record.sourceSlug}/snapshots/${record.id}`}
            target="_blank"
            rel="noreferrer"
          >
            <Button size="small">View</Button>
          </a>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <p>These are the current {qaqueue.length} sources with unassigned snapshots that require QA.</p>
      <Table
        dataSource={qaqueue as IQaQueueItem[]}
        columns={columns}
        rowKey={(record: IQaQueueItem) => record.id}
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} items`,
        }}
      />
    </div>
  );
}

export default QaQueue;
